/**
 * 绿色认证尽职调查组件
 * 步骤3.3: 绿色认证尽职调查
 * 基于 PROFESSIONAL_DUE_DILIGENCE_DESIGN.md 设计规范
 */

import React from 'react';
import {
  Form,
  Row,
  Col,
  Input,
  Select,
  Radio,
  DatePicker,
  InputNumber,
  Button,
  Progress,
  Rate,
} from 'antd';
import { UnifiedDataTable } from '../../../../components/unified/UnifiedDataTable';
import {
  EnvironmentOutlined,
  FileTextOutlined,
  SafetyCertificateOutlined,
  AlertOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  FormSection,
  FormSubSection,
  ConditionalField,
  WhenTrue,
  WhenFalse,
  RiskMatrix,
  DocumentUpload,
  useDDAutoSave,
  useDDValidation,
  DDValidationRules,
  AutoSaveIndicator,
} from './shared';
import type {
  GreenCertificationDDProps,
  GreenCertificationDDData,
  GreenProject,
  CertificationBody,
  EnvironmentalBenefit,
  ComplianceCheck,
  GreenStandard,
  RiskLevel,
} from './shared/types';

const { Option } = Select;
const { TextArea } = Input;

const GreenCertificationDD: React.FC<GreenCertificationDDProps> = ({
  projectId,
  onStepComplete,
  onStepSave,
  readonly = false,
}) => {
  const [form] = Form.useForm<GreenCertificationDDData>();

  // 使用专业尽调专用Hooks
  const { validateForm } = useDDValidation({
    form,
    stepType: 'green_cert',
  });

  const { saving, saveStatus, lastSavedText, triggerAutoSave, manualSave } =
    useDDAutoSave({
      projectId,
      stepType: 'green_cert',
      enabled: true,
      onSave: async data => {
        console.log('Green Certification DD auto saved:', data);
      },
    });

  // 监听表单变化
  const handleFormChange = () => {
    const currentValues = form.getFieldsValue();
    triggerAutoSave(currentValues);
  };

  // 保存草稿 - 通过统一操作栏触发
  const handleSaveDraft = async () => {
    const values = form.getFieldsValue();
    await manualSave(values);
    onStepSave?.('green_certification_dd', values);
  };

  // 提交表单 - 通过统一操作栏触发
  const handleSubmit = async () => {
    const result = await validateForm();
    if (result.success) {
      onStepComplete?.('green_certification_dd', result.values);
    }
  };

  // 绿色项目表格列定义
  const greenProjectColumns = [
    {
      title: '项目名称',
      dataIndex: 'projectName',
      key: 'projectName',
      width: 200,
      render: (text: string, record: GreenProject, index: number) => (
        <Input
          value={text}
          onChange={e => {
            const projects =
              form.getFieldValue(['greenProjects', 'projectList']) || [];
            const newProjects = [...projects];
            newProjects[index] = {
              ...newProjects[index],
              projectName: e.target.value,
            };
            form.setFieldValue(['greenProjects', 'projectList'], newProjects);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="绿色项目名称"
        />
      ),
    },
    {
      title: '项目类别',
      dataIndex: 'projectCategory',
      key: 'projectCategory',
      width: 150,
      render: (text: string, record: GreenProject, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const projects =
              form.getFieldValue(['greenProjects', 'projectList']) || [];
            const newProjects = [...projects];
            newProjects[index] = {
              ...newProjects[index],
              projectCategory: value,
            };
            form.setFieldValue(['greenProjects', 'projectList'], newProjects);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="项目类别"
          style={{ width: '100%' }}
        >
          <Option value="renewable_energy">可再生能源</Option>
          <Option value="energy_efficiency">能源效率</Option>
          <Option value="pollution_prevention">污染防治</Option>
          <Option value="clean_transportation">清洁交通</Option>
          <Option value="sustainable_water">可持续水资源</Option>
          <Option value="climate_adaptation">气候适应</Option>
          <Option value="circular_economy">循环经济</Option>
          <Option value="biodiversity">生物多样性保护</Option>
          <Option value="other">其他</Option>
        </Select>
      ),
    },
    {
      title: '投资金额(万元)',
      dataIndex: 'investmentAmount',
      key: 'investmentAmount',
      width: 120,
      render: (value: number, record: GreenProject, index: number) => (
        <InputNumber
          value={value}
          onChange={val => {
            const projects =
              form.getFieldValue(['greenProjects', 'projectList']) || [];
            const newProjects = [...projects];
            newProjects[index] = {
              ...newProjects[index],
              investmentAmount: val || 0,
            };
            form.setFieldValue(['greenProjects', 'projectList'], newProjects);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="投资金额"
          style={{ width: '100%' }}
          min={0}
          precision={2}
        />
      ),
    },
    {
      title: '建设期(月)',
      dataIndex: 'constructionPeriod',
      key: 'constructionPeriod',
      width: 100,
      render: (value: number, record: GreenProject, index: number) => (
        <InputNumber
          value={value}
          onChange={val => {
            const projects =
              form.getFieldValue(['greenProjects', 'projectList']) || [];
            const newProjects = [...projects];
            newProjects[index] = {
              ...newProjects[index],
              constructionPeriod: val || 0,
            };
            form.setFieldValue(['greenProjects', 'projectList'], newProjects);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="建设期"
          style={{ width: '100%' }}
          min={0}
        />
      ),
    },
    {
      title: '环境效益',
      dataIndex: 'environmentalBenefit',
      key: 'environmentalBenefit',
      width: 200,
      render: (text: string, record: GreenProject, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const projects =
              form.getFieldValue(['greenProjects', 'projectList']) || [];
            const newProjects = [...projects];
            newProjects[index] = {
              ...newProjects[index],
              environmentalBenefit: e.target.value,
            };
            form.setFieldValue(['greenProjects', 'projectList'], newProjects);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="预期环境效益"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '绿色认证状态',
      dataIndex: 'certificationStatus',
      key: 'certificationStatus',
      width: 120,
      render: (text: string, record: GreenProject, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const projects =
              form.getFieldValue(['greenProjects', 'projectList']) || [];
            const newProjects = [...projects];
            newProjects[index] = {
              ...newProjects[index],
              certificationStatus: value,
            };
            form.setFieldValue(['greenProjects', 'projectList'], newProjects);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="认证状态"
          style={{ width: '100%' }}
        >
          <Option value="certified">已认证</Option>
          <Option value="in_progress">认证中</Option>
          <Option value="planned">计划认证</Option>
          <Option value="not_applicable">不适用</Option>
        </Select>
      ),
    },
    {
      title: '合规性评分',
      dataIndex: 'complianceScore',
      key: 'complianceScore',
      width: 120,
      render: (value: number, record: GreenProject, index: number) => (
        <Rate
          value={value / 2} // 转换为5星制
          onChange={val => {
            const projects =
              form.getFieldValue(['greenProjects', 'projectList']) || [];
            const newProjects = [...projects];
            newProjects[index] = {
              ...newProjects[index],
              complianceScore: (val || 0) * 2,
            }; // 转换为10分制
            form.setFieldValue(['greenProjects', 'projectList'], newProjects);
            handleFormChange();
          }}
          disabled={readonly}
          allowHalf
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (text: any, record: GreenProject, index: number) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => {
            const projects =
              form.getFieldValue(['greenProjects', 'projectList']) || [];
            const newProjects = [...projects];
            newProjects.splice(index, 1);
            form.setFieldValue(['greenProjects', 'projectList'], newProjects);
            handleFormChange();
          }}
          disabled={readonly}
        >
          删除
        </Button>
      ),
    },
  ];

  // 认证机构表格列定义
  const certificationBodyColumns = [
    {
      title: '认证机构',
      dataIndex: 'bodyName',
      key: 'bodyName',
      width: 200,
      render: (text: string, record: CertificationBody, index: number) => (
        <Input
          value={text}
          onChange={e => {
            const bodies =
              form.getFieldValue(['certificationBodies', 'bodyList']) || [];
            const newBodies = [...bodies];
            newBodies[index] = {
              ...newBodies[index],
              bodyName: e.target.value,
            };
            form.setFieldValue(['certificationBodies', 'bodyList'], newBodies);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="认证机构名称"
        />
      ),
    },
    {
      title: '认证标准',
      dataIndex: 'certificationStandard',
      key: 'certificationStandard',
      width: 150,
      render: (text: string, record: CertificationBody, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const bodies =
              form.getFieldValue(['certificationBodies', 'bodyList']) || [];
            const newBodies = [...bodies];
            newBodies[index] = {
              ...newBodies[index],
              certificationStandard: value,
            };
            form.setFieldValue(['certificationBodies', 'bodyList'], newBodies);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="认证标准"
          style={{ width: '100%' }}
        >
          <Option value="green_bond_principles">绿色债券原则(GBP)</Option>
          <Option value="climate_bonds_standard">气候债券标准(CBS)</Option>
          <Option value="china_green_bond">中国绿色债券标准</Option>
          <Option value="eu_taxonomy">欧盟分类法</Option>
          <Option value="asean_gbs">东盟绿债标准</Option>
          <Option value="other">其他</Option>
        </Select>
      ),
    },
    {
      title: '认证范围',
      dataIndex: 'certificationScope',
      key: 'certificationScope',
      width: 200,
      render: (text: string, record: CertificationBody, index: number) => (
        <TextArea
          value={text}
          onChange={e => {
            const bodies =
              form.getFieldValue(['certificationBodies', 'bodyList']) || [];
            const newBodies = [...bodies];
            newBodies[index] = {
              ...newBodies[index],
              certificationScope: e.target.value,
            };
            form.setFieldValue(['certificationBodies', 'bodyList'], newBodies);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="认证覆盖范围"
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      ),
    },
    {
      title: '有效期',
      dataIndex: 'validityPeriod',
      key: 'validityPeriod',
      width: 120,
      render: (text: string, record: CertificationBody, index: number) => (
        <DatePicker
          value={text ? dayjs(text) : null}
          onChange={date => {
            const bodies =
              form.getFieldValue(['certificationBodies', 'bodyList']) || [];
            const newBodies = [...bodies];
            newBodies[index] = {
              ...newBodies[index],
              validityPeriod: date ? date.format('YYYY-MM-DD') : '',
            };
            form.setFieldValue(['certificationBodies', 'bodyList'], newBodies);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="有效期"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '认证状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (text: string, record: CertificationBody, index: number) => (
        <Select
          value={text}
          onChange={value => {
            const bodies =
              form.getFieldValue(['certificationBodies', 'bodyList']) || [];
            const newBodies = [...bodies];
            newBodies[index] = { ...newBodies[index], status: value };
            form.setFieldValue(['certificationBodies', 'bodyList'], newBodies);
            handleFormChange();
          }}
          disabled={readonly}
          placeholder="状态"
          style={{ width: '100%' }}
        >
          <Option value="active">有效</Option>
          <Option value="expired">已过期</Option>
          <Option value="suspended">暂停</Option>
          <Option value="revoked">撤销</Option>
        </Select>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      render: (text: any, record: CertificationBody, index: number) => (
        <Button
          type="link"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => {
            const bodies =
              form.getFieldValue(['certificationBodies', 'bodyList']) || [];
            const newBodies = [...bodies];
            newBodies.splice(index, 1);
            form.setFieldValue(['certificationBodies', 'bodyList'], newBodies);
            handleFormChange();
          }}
          disabled={readonly}
        >
          删除
        </Button>
      ),
    },
  ];

  return (
    <div>
      {/* 自动保存指示器 */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold">绿色认证尽职调查</h2>
        <AutoSaveIndicator
          saving={saving}
          saveStatus={saveStatus}
          lastSavedText={lastSavedText}
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
        disabled={readonly}
      >
        {/* A. 绿色项目识别与分类 */}
        <FormSection
          title="绿色项目识别与分类"
          icon={<EnvironmentOutlined />}
          alert={{
            message: '请全面识别和分类所有绿色项目，确保符合绿色债券标准',
            type: 'info',
          }}
        >
          <FormSubSection title="绿色项目清单">
            <Form.Item
              name={['greenProjects', 'projectList']}
              initialValue={[]}
            >
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const projects =
                    form.getFieldValue(['greenProjects', 'projectList']) || [];
                  return (
                    <>
                      <UnifiedDataTable
                        columns={greenProjectColumns}
                        dataSource={projects}
                        pagination={false}
                        size="small"
                        rowKey={(record, index) => `green-project-${index}`}
                        locale={{
                          emptyText: '暂无绿色项目记录，请点击下方按钮添加',
                        }}
                        scroll={{ x: 'max-content' }}
                      />
                      <Button
                        type="dashed"
                        onClick={() => {
                          const newProjects = [
                            ...projects,
                            {
                              projectName: '',
                              projectCategory: '',
                              investmentAmount: 0,
                              constructionPeriod: 0,
                              environmentalBenefit: '',
                              certificationStatus: '',
                              complianceScore: 8,
                            },
                          ];
                          form.setFieldValue(
                            ['greenProjects', 'projectList'],
                            newProjects
                          );
                          handleFormChange();
                        }}
                        icon={<PlusOutlined />}
                        style={{ width: '100%', marginTop: 8 }}
                        disabled={readonly}
                      >
                        添加绿色项目
                      </Button>
                    </>
                  );
                }}
              </Form.Item>
            </Form.Item>
          </FormSubSection>

          <FormSubSection title="项目分类统计">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['greenProjects', 'statistics', 'totalProjects']}
                  label="项目总数"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    placeholder="绿色项目总数"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['greenProjects', 'statistics', 'totalInvestment']}
                  label="总投资额(万元)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.amount(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="总投资金额"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'greenProjects',
                    'statistics',
                    'averageComplianceScore',
                  ]}
                  label="平均合规评分"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    max={10}
                    precision={1}
                    placeholder="平均合规评分"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>
        </FormSection>

        {/* B. 绿色认证机构评估 */}
        <FormSection
          title="绿色认证机构评估"
          icon={<SafetyCertificateOutlined />}
          alert={{
            message: '请评估第三方绿色认证机构的资质和认证质量',
            type: 'warning',
          }}
        >
          <FormSubSection title="认证机构清单">
            <Form.Item
              name={['certificationBodies', 'bodyList']}
              initialValue={[]}
            >
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const bodies =
                    form.getFieldValue(['certificationBodies', 'bodyList']) ||
                    [];
                  return (
                    <>
                      <UnifiedDataTable
                        columns={certificationBodyColumns}
                        dataSource={bodies}
                        pagination={false}
                        size="small"
                        rowKey={(record, index) => `cert-body-${index}`}
                        locale={{
                          emptyText: '暂无认证机构记录，请点击下方按钮添加',
                        }}
                        scroll={{ x: 'max-content' }}
                      />
                      <Button
                        type="dashed"
                        onClick={() => {
                          const newBodies = [
                            ...bodies,
                            {
                              bodyName: '',
                              certificationStandard: '',
                              certificationScope: '',
                              validityPeriod: '',
                              status: 'active',
                            },
                          ];
                          form.setFieldValue(
                            ['certificationBodies', 'bodyList'],
                            newBodies
                          );
                          handleFormChange();
                        }}
                        icon={<PlusOutlined />}
                        style={{ width: '100%', marginTop: 8 }}
                        disabled={readonly}
                      >
                        添加认证机构
                      </Button>
                    </>
                  );
                }}
              </Form.Item>
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* C. 环境效益量化分析 */}
        <FormSection
          title="环境效益量化分析"
          icon={<CheckCircleOutlined />}
          alert={{
            message: '请量化分析绿色项目的环境效益和社会效益',
            type: 'info',
          }}
        >
          <FormSubSection title="碳减排效益">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={[
                    'environmentalBenefits',
                    'carbonReduction',
                    'annualReduction',
                  ]}
                  label="年减排量(吨CO2)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.amount(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="年度碳减排量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'environmentalBenefits',
                    'carbonReduction',
                    'totalReduction',
                  ]}
                  label="总减排量(吨CO2)"
                  rules={[
                    DDValidationRules.required(),
                    DDValidationRules.amount(),
                  ]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="项目生命周期总减排量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'environmentalBenefits',
                    'carbonReduction',
                    'reductionIntensity',
                  ]}
                  label="减排强度(吨CO2/万元)"
                  rules={[DDValidationRules.required()]}
                >
                  <InputNumber
                    min={0}
                    precision={4}
                    placeholder="单位投资减排强度"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>

          <FormSubSection title="其他环境效益">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={[
                    'environmentalBenefits',
                    'otherBenefits',
                    'energySaving',
                  ]}
                  label="节能效益(万千瓦时/年)"
                  rules={[DDValidationRules.amount()]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="年度节能量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={[
                    'environmentalBenefits',
                    'otherBenefits',
                    'waterSaving',
                  ]}
                  label="节水效益(万吨/年)"
                  rules={[DDValidationRules.amount()]}
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    placeholder="年度节水量"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name={[
                'environmentalBenefits',
                'otherBenefits',
                'pollutionReduction',
              ]}
              label="污染减排效益"
            >
              <TextArea
                rows={3}
                placeholder="请描述项目在大气、水体、土壤等方面的污染减排效益"
              />
            </Form.Item>

            <Form.Item
              name={[
                'environmentalBenefits',
                'otherBenefits',
                'biodiversityProtection',
              ]}
              label="生物多样性保护效益"
            >
              <TextArea
                rows={3}
                placeholder="请描述项目对生物多样性保护的积极作用"
              />
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* D. 绿色标准合规性检查 */}
        <FormSection
          title="绿色标准合规性检查"
          icon={<FileTextOutlined />}
          alert={{
            message: '请检查项目是否符合相关绿色标准和监管要求',
            type: 'warning',
          }}
        >
          <FormSubSection title="国际标准合规性">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name={['complianceCheck', 'international', 'gbpCompliance']}
                  label="绿色债券原则(GBP)合规"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>合规</Radio>
                    <Radio value={false}>不合规</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={['complianceCheck', 'international', 'cbsCompliance']}
                  label="气候债券标准(CBS)合规"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>合规</Radio>
                    <Radio value={false}>不合规</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={[
                    'complianceCheck',
                    'international',
                    'euTaxonomyCompliance',
                  ]}
                  label="欧盟分类法合规"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>合规</Radio>
                    <Radio value={false}>不合规</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>

          <FormSubSection title="国内标准合规性">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name={[
                    'complianceCheck',
                    'domestic',
                    'chinaGreenBondCompliance',
                  ]}
                  label="中国绿色债券标准合规"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>合规</Radio>
                    <Radio value={false}>不合规</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={[
                    'complianceCheck',
                    'domestic',
                    'localRegulationCompliance',
                  ]}
                  label="地方监管要求合规"
                  rules={[DDValidationRules.required()]}
                >
                  <Radio.Group>
                    <Radio value={true}>合规</Radio>
                    <Radio value={false}>不合规</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </FormSubSection>
        </FormSection>

        {/* E. 绿色认证风险评估 */}
        <FormSection
          title="绿色认证风险评估"
          icon={<AlertOutlined />}
          alert={{
            message: '请基于绿色认证尽职调查结果，全面评估绿色认证风险',
            type: 'info',
          }}
        >
          <FormSubSection title="绿色认证风险矩阵">
            <Form.Item
              name={['greenCertificationRisk', 'riskMatrix']}
              initialValue={[]}
            >
              <RiskMatrix
                riskCategories={[
                  { value: 'greenwashing_risk', label: '漂绿风险' },
                  {
                    value: 'certification_validity_risk',
                    label: '认证有效性风险',
                  },
                  {
                    value: 'environmental_benefit_risk',
                    label: '环境效益风险',
                  },
                  { value: 'compliance_risk', label: '合规风险' },
                  { value: 'monitoring_risk', label: '监测风险' },
                  { value: 'reputation_risk', label: '声誉风险' },
                ]}
                readonly={readonly}
                emptyText="暂无绿色认证风险评估，请点击下方按钮添加"
                addButtonText="添加绿色认证风险"
                showResponsibleParty={true}
                showTimeline={true}
                onChange={value => {
                  form.setFieldValue(
                    ['greenCertificationRisk', 'riskMatrix'],
                    value
                  );
                  handleFormChange();
                }}
              />
            </Form.Item>
          </FormSubSection>

          <FormSubSection title="综合绿色认证结论">
            <Form.Item
              name={[
                'greenCertificationRisk',
                'overallAssessment',
                'certificationQuality',
              ]}
              label="绿色认证质量评估"
              rules={[DDValidationRules.required()]}
            >
              <Select placeholder="请选择绿色认证质量评估结论">
                <Option value="excellent">认证质量优秀</Option>
                <Option value="good">认证质量良好</Option>
                <Option value="acceptable">认证质量可接受</Option>
                <Option value="concerns">存在质量关注</Option>
                <Option value="inadequate">认证质量不足</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name={[
                'greenCertificationRisk',
                'overallAssessment',
                'detailedAssessment',
              ]}
              label="详细绿色认证评估"
              rules={[
                DDValidationRules.required(),
                DDValidationRules.legalOpinion(100),
              ]}
            >
              <TextArea
                rows={6}
                placeholder="请详细阐述绿色认证尽职调查的结论和建议（至少100字）"
              />
            </Form.Item>

            <Form.Item
              name={[
                'greenCertificationRisk',
                'overallAssessment',
                'recommendations',
              ]}
              label="改进建议"
            >
              <TextArea rows={4} placeholder="请提出具体的绿色认证改进建议" />
            </Form.Item>
          </FormSubSection>
        </FormSection>

        {/* 文档上传区域 */}
        <FormSection
          title="相关文档"
          icon={<FileTextOutlined />}
          alert={{
            message:
              '发行人和中介机构都可以上传相关文档。发行人提供基础材料，中介机构补充尽职调查报告和专业意见。',
            type: 'info',
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <DocumentUpload
                projectId={projectId}
                stepId="green_cert_dd_certificates"
                title="绿色认证文件"
                description="绿色认证证书、评估报告、第三方意见等"
                maxFileSize={20}
                maxFileCount={15}
                allowedFileTypes={['.pdf', '.doc', '.docx', '.jpg', '.png']}
                readonly={readonly}
              />
            </Col>
            <Col span={12}>
              <DocumentUpload
                projectId={projectId}
                stepId="green_cert_dd_environmental"
                title="环境效益文件"
                description="环境影响评估、碳减排报告、监测数据等"
                maxFileSize={20}
                maxFileCount={20}
                allowedFileTypes={['.pdf', '.doc', '.docx', '.xls', '.xlsx']}
                readonly={readonly}
              />
            </Col>
          </Row>
        </FormSection>

        {/* 禁止在组件内部添加操作按钮！ */}
        {/* 所有操作必须通过统一底部操作栏处理 */}
      </Form>
    </div>
  );
};

export default GreenCertificationDD;
